import React, { createContext, useContext, useRef, useState, useEffect, useCallback } from "react";
import { useShareNudge } from "@/components/site/useShareNudge";

const PlayerContext = createContext(null);
export const usePlayer = () => useContext(PlayerContext);

/**
 * A single, app-wide <audio> element that persists across client-side navigation.
 * Previews are 90s server-cut derivatives (from 0:00); an optional previewCap adds a
 * safety stop. Starting audio pauses any playing video (and vice-versa) via window events.
 */
export const PREVIEW_SECONDS = 90;
export const PREVIEW_LABEL = "90-second";
export const PlayerProvider = ({ children }) => {
  const audioRef = useRef(null);
  const [track, setTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);

  const cap = track?.previewCap || 0;
  const span = cap || duration || PREVIEW_SECONDS;
  const fraction = span > 0 ? Math.min(current / span, 1) : 0;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;
    const onTime = () => {
      if (cap && audio.currentTime >= cap) { audio.pause(); audio.currentTime = 0; setCurrent(cap); setIsPlaying(false); return; }
      setCurrent(audio.currentTime);
    };
    const onMeta = () => setDuration(audio.duration || 0);
    const onEnd = () => { setIsPlaying(false); setCurrent(0); };
    const onPlay = () => { setIsPlaying(true); window.dispatchEvent(new CustomEvent("hns:audio-play")); };
    const onPause = () => setIsPlaying(false);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("ended", onEnd);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("ended", onEnd);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
    };
  }, [track, cap]);

  // Pause audio when a video starts anywhere on the site.
  useEffect(() => {
    const onVideo = () => { const a = audioRef.current; if (a && !a.paused) a.pause(); };
    window.addEventListener("hns:video-play", onVideo);
    return () => window.removeEventListener("hns:video-play", onVideo);
  }, []);

  const playTrack = useCallback((next, startAt = 0) => {
    const audio = audioRef.current;
    if (!audio || !next?.src) return;
    setHasStarted(true);
    if (!track || track.id !== next.id) { setTrack(next); audio.src = next.src; audio.load(); }
    audio.currentTime = startAt;
    setCurrent(startAt);
    const p = audio.play();
    if (p && p.catch) p.catch(() => setIsPlaying(false));
  }, [track]);

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !track) return;
    if (audio.paused) { const p = audio.play(); if (p && p.catch) p.catch(() => {}); } else audio.pause();
  }, [track]);

  const pause = useCallback(() => { const a = audioRef.current; if (a && !a.paused) a.pause(); }, []);

  const seek = useCallback((frac) => {
    const audio = audioRef.current;
    if (!audio || !track) return;
    const t = Math.max(0, Math.min(frac, 1)) * span;
    audio.currentTime = t; setCurrent(t);
  }, [track, span]);

  const value = { track, isPlaying, current, duration, span, fraction, hasStarted,
    playTrack, toggle, pause, seek, isActive: (id) => track?.id === id };

  useShareNudge(track, isPlaying, current);

  return (
    <PlayerContext.Provider value={value}>
      {children}
      <audio ref={audioRef} preload="none" />
    </PlayerContext.Provider>
  );
};
